export const PRESENTATION_LIMITS = Object.freeze({
  maxDeckBytes: 100 * 1024 * 1024,
  maxPptxBytes: 80 * 1024 * 1024,
  maxPdfBytes: 80 * 1024 * 1024,
  maxOfficeBytes: 25 * 1024 * 1024,
  maxTextBytes: 8 * 1024 * 1024,
  maxTableBytes: 12 * 1024 * 1024,
  maxImageBytes: 15 * 1024 * 1024,
  maxImages: 100,
});

export const PRESENTATION_ACCEPT = [
  '.pptx', '.pptm', '.ppt',
  '.pdf',
  '.docx', '.docm', '.doc',
  '.txt', '.text', '.md', '.markdown', '.rtf',
  '.xlsx', '.xlsm', '.xls', '.csv', '.tsv',
  '.odt', '.ods', '.odp', '.key', '.pages', '.numbers',
  'image/png', 'image/jpeg', 'image/webp', 'image/gif',
].join(',');

export const extensionOf = (file = {}) => {
  const name = String(file.name || '').trim().toLowerCase();
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot) : '';
};

const guidanceByExtension = Object.freeze({
  '.ppt': 'Legacy PowerPoint (.ppt) cannot be faithfully rendered in this browser. Convert it to PPTX or PDF, or open it locally and share that application window.',
  '.xls': 'Legacy Excel (.xls) cannot be faithfully rendered in this browser. Convert it to XLSX, CSV, or PDF, or open it locally and share that application window.',
  '.odt': 'OpenDocument Text preview is not available here. Export it as PDF or DOCX, or open it locally and share that application window.',
  '.ods': 'OpenDocument Spreadsheet preview is not available here. Export it as PDF, XLSX, or CSV, or open it locally and share that application window.',
  '.odp': 'OpenDocument Presentation preview is not available here. Export it as PDF or PPTX, or open it locally and share that application window.',
  '.key': 'Apple Keynote files cannot be rendered faithfully in this browser. Export as PDF or PPTX, or open Keynote and share that application window.',
  '.pages': 'Apple Pages files cannot be rendered faithfully in this browser. Export as PDF or DOCX, or open Pages and share that application window.',
  '.numbers': 'Apple Numbers files cannot be rendered faithfully in this browser. Export as PDF, XLSX, or CSV, or open Numbers and share that application window.',
});

export const getPresentationGuidance = (file) =>
  guidanceByExtension[extensionOf(file)] ||
  'This office format cannot be rendered faithfully in this browser. Convert it to PDF or another supported format, or open it locally and share that application window.';

export const classifyPresentationFile = (file) => {
  const extension = extensionOf(file);
  const mime = String(file?.type || '').toLowerCase();

  if (
    ['.pptx', '.pptm'].includes(extension) ||
    mime === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    mime === 'application/vnd.ms-powerpoint.presentation.macroenabled.12'
  ) return 'pptx';
  if (extension === '.ppt' || mime === 'application/vnd.ms-powerpoint') return 'guidance';
  if (extension === '.pdf' || mime === 'application/pdf') return 'pdf';
  if (
    ['.docx', '.docm'].includes(extension) ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mime === 'application/vnd.ms-word.document.macroenabled.12'
  ) return 'docx';
  if (extension === '.doc' || mime === 'application/msword') return 'doc';
  if (['.txt', '.text', '.md', '.markdown'].includes(extension) || mime === 'text/plain' || mime === 'text/markdown') return 'text';
  if (extension === '.rtf' || mime === 'application/rtf' || mime === 'text/rtf') return 'rtf';
  if (
    ['.xlsx', '.xlsm'].includes(extension) ||
    mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mime === 'application/vnd.ms-excel.sheet.macroenabled.12'
  ) return 'xlsx';
  if (extension === '.xls' || mime === 'application/vnd.ms-excel') return 'guidance';
  if (extension === '.csv' || mime === 'text/csv') return 'csv';
  if (extension === '.tsv' || mime === 'text/tab-separated-values') return 'tsv';
  if (
    ['.png', '.jpg', '.jpeg', '.webp', '.gif'].includes(extension) ||
    ['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(mime)
  ) return 'image';
  if (['.odt', '.ods', '.odp', '.key', '.pages', '.numbers'].includes(extension)) return 'guidance';
  return 'unsupported';
};

export const relayKindForPresentation = (kind, file) => {
  if (kind === 'guidance') {
    const legacy = extensionOf(file).replace('.', '');
    return ['ppt', 'xls'].includes(legacy) ? legacy : '';
  }
  if (kind === 'text') return extensionOf(file) === '.text' ? 'text' : extensionOf(file) === '.md' || extensionOf(file) === '.markdown' ? 'md' : 'txt';
  return ['pptx', 'pdf', 'docx', 'doc', 'rtf', 'xlsx', 'csv', 'tsv', 'image'].includes(kind) ? kind : '';
};

const result = (valid, values = {}) => ({
  valid,
  kind: null,
  files: [],
  error: '',
  ...values,
});

export const validatePresentationFiles = (fileList) => {
  const files = Array.from(fileList || []).filter(Boolean);
  if (!files.length) return result(false, { error: 'Choose a presentation or document file.' });

  const kinds = files.map(classifyPresentationFile);
  if (kinds.includes('unsupported')) {
    return result(false, {
      files,
      error: 'Choose a supported presentation, PDF, Word, text, spreadsheet, image, or common office file.',
    });
  }

  const totalBytes = files.reduce((sum, file) => sum + Number(file.size || 0), 0);
  if (totalBytes > PRESENTATION_LIMITS.maxDeckBytes) {
    return result(false, { files, error: 'The selected presentation exceeds the 100 MB local limit.' });
  }

  const uniqueKinds = new Set(kinds);
  if (uniqueKinds.size !== 1) {
    return result(false, { files, error: 'Do not mix presentation formats in one local deck.' });
  }

  const kind = kinds[0];
  if (kind !== 'image' && files.length !== 1) {
    return result(false, { files, error: 'Choose one document at a time, or choose multiple slide images.' });
  }
  if (kind === 'pptx' && Number(files[0].size || 0) > PRESENTATION_LIMITS.maxPptxBytes) {
    return result(false, { files, error: 'PPTX files must be 80 MB or smaller.' });
  }
  if (kind === 'pdf' && Number(files[0].size || 0) > PRESENTATION_LIMITS.maxPdfBytes) {
    return result(false, { files, error: 'PDF files must be 80 MB or smaller.' });
  }
  if (['docx', 'doc', 'xlsx'].includes(kind) && Number(files[0].size || 0) > PRESENTATION_LIMITS.maxOfficeBytes) {
    return result(false, { files, error: 'Word and XLSX presentations must be 25 MB or smaller.' });
  }
  if (['text', 'rtf'].includes(kind) && Number(files[0].size || 0) > PRESENTATION_LIMITS.maxTextBytes) {
    return result(false, { files, error: 'Text and RTF previews must be 8 MB or smaller.' });
  }
  if (['csv', 'tsv'].includes(kind) && Number(files[0].size || 0) > PRESENTATION_LIMITS.maxTableBytes) {
    return result(false, { files, error: 'CSV and TSV previews must be 12 MB or smaller.' });
  }
  if (kind === 'image') {
    if (files.length > PRESENTATION_LIMITS.maxImages) {
      return result(false, { files, error: 'Choose no more than 100 slide images.' });
    }
    if (files.some((file) => Number(file.size || 0) > PRESENTATION_LIMITS.maxImageBytes)) {
      return result(false, { files, error: 'Each slide image must be 15 MB or smaller.' });
    }
  }

  return result(true, {
    kind,
    files,
    relayKind: relayKindForPresentation(kind, files[0]),
    guidance: kind === 'guidance' ? getPresentationGuidance(files[0]) : '',
  });
};

export const createLocalDeckId = () => {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {}
  return `deck-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};
